import { useCallback, useState } from "react";
import { useInfiniteQuery } from "react-query";
import { useSelector } from "react-redux";
import { Selectors as UserSelectors } from "@/store/user/selectors";
import { Selectors as AuthSelectors } from "@/store/auth/selectors";
import { TasksAPI } from "@/api/tasks";
import { useParsedQuery } from "@/hooks/useParsedQuery";
import isempty from "lodash/isEmpty";
import { atomWithStorage } from "jotai/utils";
import { useAtom } from "jotai";
import { TasksCacheService } from "@/services/tasks/cache";

const sortByAtom = atomWithStorage("orders-page-sort", "-start_date");
export const useOrdersSortBy = () => {
  const query = useParsedQuery();
  const [value, setValue] = useAtom(sortByAtom);
  return [query.sort_by || value, setValue];
};
export const useClientProfile = () => {
  const [clientProfileState, setState] = useState({
    isOpen: false,
    client: null,
  });
  const openClientProfile = useCallback((client) => {
    setState({
      isOpen: true,
      client,
    });
  }, []);
  const closeClientProfile = useCallback(() => {
    setState((state) => ({
      isOpen: false,
      client: state.client,
    }));
  }, []);
  return { clientProfileState, openClientProfile, closeClientProfile };
};

const useQueryExtraParams = (mode) => {
  const isMaster = useSelector(UserSelectors.isBusinessMode);
  const userId = useSelector(UserSelectors.getUserId);
  const masterId = useSelector(UserSelectors.getMasterId);
  const role = {
    [isMaster ? "master" : "consumer"]: isMaster ? masterId : userId,
  };
  switch (mode) {
    case "all":
      return role;
    case "completed":
      return {
        ...role,
        status: "DONE",
      };
    case "active":
      return {
        ...role,
        status: "ACCEPTED",
      };
    case "personal":
      return {
        ...role,
        status: "PERSONAL",
      };
    case "confirm":
      return {
        ...role,
        status: "PENDING",
      };
    case "my_orders":
      return {
        consumer: userId,
      };
    case "need_to_pay":
      return {
        ...role,
        status: "PAY_WAITING",
      };
    case "canceled":
      return {
        ...role,
        status: "CANCELLED",
      };
    case "dispute":
      return {
        ...role,
        status: "DISPUTE",
      };
    default:
      return role;
  }
};

const PER_PAGE = 10;
export const useTasksQuery = (mode) => {
  const query = useParsedQuery();
  const isAuth = useSelector(AuthSelectors.isAuth);
  const extraParams = useQueryExtraParams(mode);
  const [sort_by] = useOrdersSortBy();
  const finalParams = {
    ...extraParams,
    ...query,
    sort_by,
    limit: PER_PAGE,
  };
  const loadTasks = async ({ pageParam = 0 }) => {
    const { data: tasks } = await TasksAPI.getTasks({
      ...finalParams,
      page: pageParam,
    });

    return {
      tasks,
      next: isempty(tasks) ? undefined : pageParam + 1,
    };
  };
  const reactQuery = useInfiniteQuery(
    TasksCacheService.buildTasksSearchQueryKey(finalParams),
    loadTasks,
    {
      staleTime: 0,
      enabled: !!mode && isAuth,
      getNextPageParam: (last) => last?.next,
    }
  );
  return reactQuery;
};

const defaultState = {
  isOpen: false,
  task: null,
  personal: false,
};
export const useTaskEditorState = () => {
  const [state, setState] = useState(defaultState);
  const open = useCallback(({ task = null, personal } = {}) => {
    setState({
      isOpen: true,
      task,
      personal,
    });
  }, []);
  const close = useCallback(() => {
    setState(defaultState);
  }, []);
  return {
    isOpen: state.isOpen,
    task: state.task,
    personal: state.personal,
    open,
    close,
  };
};
